import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickGovernanceEntry } from '../../shared/governanceResultsAccess';
import PhoenixDocCard from 'components/base/PhoenixDocCard';

interface Control {
  code: string;
  name: string;
  score: number;
  status: 'PASS' | 'FAIL';
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  threshold: number;
  owasp_category: string;
  owasp_description: string;
  owasp_reference: string;
}

interface OwaspResponse {
  project_id: string;
  overall_status: string;
  controls: Control[];
  graph_base64?: string;
}

interface OwaspTabProps {
  projectId: string;
  userId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OwaspTab = (_props: OwaspTabProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<OwaspResponse | null>(null);

  const owaspData = useMemo(
    () => pickGovernanceEntry(projectResults, ['owasp_mlp', 'owasp']) as OwaspResponse | null,
    [projectResults]
  );

  useEffect(() => {
    setData(owaspData ?? null);
  }, [owaspData]);

  const getStatusBadge = (status: string) => {
    return status === 'PASS' ? (
      <Badge bg="success">
        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
        PASS
      </Badge>
    ) : (
      <Badge bg="danger">
        <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
        FAIL
      </Badge>
    );
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'MEDIUM':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'LOW':
        return <Badge bg="info">LOW</Badge>;
      default:
        return <Badge bg="secondary">{severity}</Badge>;
    }
  };

  return (
    <div className="pt-4">
      <div className="mb-4">
        <h3 className="mb-1">OWASP MLP Controls</h3>
        <p className="text-muted mb-0">Project controls analyzed against OWASP standards.</p>
      </div>

      {data && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <PhoenixDocCard>
                <PhoenixDocCard.Header title="Analysis Overview">
                  <div className="d-flex align-items-center">
                    <span className="me-2">Overall Status:</span>
                    {data.overall_status === 'AT_RISK' ? (
                      <Badge bg="danger" className="fs-6">
                        AT RISK
                      </Badge>
                    ) : (
                      <Badge bg="success" className="fs-6">
                        PASS
                      </Badge>
                    )}
                  </div>
                </PhoenixDocCard.Header>
                <PhoenixDocCard.Body>
                  {data.graph_base64 && (
                    <div className="text-center mb-4">
                      <img
                        src={`data:image/png;base64,${data.graph_base64}`}
                        alt="OWASP Control Graph"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          maxHeight: '500px',
                          borderRadius: '8px',
                          border: '1px solid #eee'
                        }}
                      />
                    </div>
                  )}
                </PhoenixDocCard.Body>
              </PhoenixDocCard>
            </Col>
          </Row>

          <PhoenixDocCard>
            <PhoenixDocCard.Header title="Control Metrics" />
            <PhoenixDocCard.Body>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Control</th>
                    <th>OWASP Category</th>
                    <th>Score</th>
                    <th>Threshold</th>
                    <th>Severity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.controls.map(control => (
                    <tr key={control.code}>
                      <td>
                        <div className="fw-bold">{control.name}</div>
                        <small className="text-muted">{control.code}</small>
                      </td>
                      <td>
                        <div className="fw-semibold">{control.owasp_category}</div>
                        <small
                          className="text-muted d-block text-truncate"
                          style={{ maxWidth: '300px' }}
                          title={control.owasp_description}
                        >
                          {control.owasp_description}
                        </small>
                      </td>
                      <td>{control.score}</td>
                      <td>{control.threshold}</td>
                      <td>{getSeverityBadge(control.severity)}</td>
                      <td>{getStatusBadge(control.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </PhoenixDocCard.Body>
          </PhoenixDocCard>
        </>
      )}

      {!data && (
        <div className="text-center py-5 text-muted">
          <FontAwesomeIcon icon={faShieldAlt} size="3x" className="mb-3 text-secondary" />
          <h5>No Analysis Data</h5>
          <p>No OWASP analysis data available for this project.</p>
        </div>
      )}
    </div>
  );
};

export default OwaspTab;
